import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import { format, startOfMonth, subMonths } from 'date-fns';
import SaleModal from '../components/SaleModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDollarSign, FiTrendingUp, FiCalendar, FiUser, FiCar, FiPlus } = FiIcons;

function Sales() {
  const { state } = useCustomers();
  const { sales } = state;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const thisMonth = startOfMonth(new Date());
  const lastMonth = startOfMonth(subMonths(new Date(), 1));

  const thisMonthSales = sales.filter(sale =>
    new Date(sale.saleDate) >= thisMonth
  );

  const lastMonthSales = sales.filter(sale =>
    new Date(sale.saleDate) >= lastMonth && new Date(sale.saleDate) < thisMonth
  );

  const thisMonthRevenue = thisMonthSales.reduce((total, sale) => total + (sale.salePrice || 0), 0);
  const lastMonthRevenue = lastMonthSales.reduce((total, sale) => total + (sale.salePrice || 0), 0);

  const revenueChange = lastMonthRevenue > 0 ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 : 0;

  const stats = [
    {
      name: 'Total Sales',
      value: sales.length,
      icon: FiDollarSign,
      color: 'bg-green-500',
      change: `${thisMonthSales.length - lastMonthSales.length} this month`,
    },
    {
      name: 'Monthly Revenue',
      value: `$${thisMonthRevenue.toLocaleString()}`,
      icon: FiTrendingUp,
      color: 'bg-blue-500',
      change: `${revenueChange > 0 ? '+' : ''}${revenueChange.toFixed(1)}% vs last month`,
    },
    {
      name: 'Average Sale',
      value: sales.length > 0
        ? `$${Math.round(sales.reduce((total, sale) => total + (sale.salePrice || 0), 0) / sales.length).toLocaleString()}`
        : '$0',
      icon: FiCalendar,
      color: 'bg-purple-500',
      change: 'Per transaction',
    },
    {
      name: 'This Month',
      value: thisMonthSales.length,
      icon: FiUser,
      color: 'bg-orange-500',
      change: 'Vehicles sold',
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{stat.name}</p>
                <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mt-1 sm:mt-2 truncate">{stat.value}</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">{stat.change}</p>
              </div>
              <div className={`${stat.color} p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2`}>
                <SafeIcon icon={stat.icon} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Sale Button */}
      <div className="flex justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsModalOpen(true)}
          className="bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2 text-sm sm:text-base"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span className="hidden sm:inline">Record Sale</span>
          <span className="sm:hidden">Add</span>
        </motion.button>
      </div>

      {/* Sales List */}
      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Sales</h3>
        </div>
        {sales.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Vehicle
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sale Price
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sales
                  .sort((a, b) => new Date(b.saleDate) - new Date(a.saleDate))
                  .map((sale, index) => (
                    <motion.tr
                      key={sale.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-xs sm:text-sm">
                              {sale.customerName?.split(' ').map(n => n.charAt(0)).join('') || 'N/A'}
                            </span>
                          </div>
                          <div className="ml-3 sm:ml-4">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                              {sale.customerName || 'Unknown Customer'}
                            </div>
                            <div className="text-xs text-gray-500 sm:hidden truncate">
                              {sale.vehicleDetails || 'Vehicle details not available'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="flex items-center">
                          <SafeIcon icon={FiCar} className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mr-2" />
                          <div className="text-xs sm:text-sm text-gray-900 truncate max-w-xs">
                            {sale.vehicleDetails || 'Vehicle details not available'}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm font-medium text-green-600">
                          ${sale.salePrice?.toLocaleString() || '0'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden md:table-cell">
                        {format(new Date(sale.saleDate), 'MMM d, yyyy')}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          {sale.status || 'Completed'}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <SafeIcon icon={FiDollarSign} className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No sales recorded</h3>
            <p className="text-gray-500 mb-6 text-sm sm:text-base px-4">
              Start tracking your sales transactions to monitor revenue and performance
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsModalOpen(true)}
              className="bg-primary-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors text-sm sm:text-base"
            >
              Record First Sale
            </motion.button>
          </div>
        )}
      </div>

      {/* Sale Modal */}
      <SaleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

export default Sales;